import { ENTITY_CONFIGS } from './entity-configs.js'
import { resolveIncidentGroup } from './incident-category-resolver.js'
import { readCssVar } from '../utils/map-constants.js'

// Mapping slug CNIE → token del tema usado para el halo del marker.
// Apunta directo a los tokens cat-<slug> de las paletas declarativas
// (theming/palettes/*.js) — fuente única de verdad de la paleta del
// manual federal del 911. Halo, fondo del icono y chip del drawer se
// perciben como un solo color por categoría.
var INCIDENT_HALO_TOKEN = {
  seguridad:          'cat-seguridad',
  medico:             'cat-medico',
  proteccion_civil:   'cat-proteccion-civil',
  servicios_publicos: 'cat-servicios-publicos',
  asistencia:         'cat-asistencia',
  otros_servicios:    'cat-otros-servicios',
  mal_uso:            'cat-mal-uso',
}

/**
 * Normalize a raw item into a MapMonitor.
 * @param {string} entityType - key from ENTITY_CONFIGS
 * @param {Object} item - raw data from API/WS
 * @returns {Object|null} MapMonitor or null if coords missing
 */
export function normalizeItem(entityType, item) {
  const config = ENTITY_CONFIGS[entityType]
  if (!config) {
    console.warn('[normalizer] Unknown entity type:', entityType)
    return null
  }

  const coords = config.getCoords(item)
  if (!coords || coords.length < 2) return null
  // Validate coordinate ranges (GeoJSON: [lng, lat])
  var lng = coords[0], lat = coords[1]
  if (isNaN(lng) || isNaN(lat)) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null

  // Algunos tipos (reportant) no tienen id Mongo estable; el config
  // provee un getId custom que usa otro campo (ej. phone).
  const rawId = config.getId ? config.getId(item) : (item.id || item._id)
  return {
    id: entityType + '-' + rawId,
    entityType,
    title: config.getTitle(item),
    subtitle: config.getSubtitle(item),
    coordinates: coords,
    status: config.getStatus(item),
    icon: config.getIcon(item),
    color: config.color,
    isStatic: config.isStatic,
    // Sistema externo de interconexión (carbyne / didi / uber /
    // rapidsos / null). Solo lo emiten configs que implementan
    // getPartner — hoy reportant e incident; las demás entidades
    // pasan null y el badge layer las filtra por `['has', 'partner']`.
    partner: config.getPartner ? (config.getPartner(item) || null) : null,
    data: item,
  }
}

/**
 * Normalize an array of raw items.
 * @param {string} entityType
 * @param {Object[]} items
 * @returns {Object[]} array of MapMonitors
 */
export function normalizeItems(entityType, items) {
  const results = []
  for (const item of items) {
    const monitor = normalizeItem(entityType, item)
    if (monitor) results.push(monitor)
  }
  return results
}

/**
 * Convert a MapMonitor to a GeoJSON Feature for MapLibre.
 * @param {Object} monitor - MapMonitor
 * @param {string} mapMode - 'day' or 'night'
 * @returns {Object} GeoJSON Feature
 */
// Normaliza `attributes.prioridad` del backend (viene con variaciones como
// "ALTA"/"alta"/"High"/null) al enum interno usado por MARKERS-DESIGN-SPEC.
function normalizePriority(v) {
  if (!v || typeof v !== 'string') return null
  var s = v.trim().toUpperCase()
  if (s === 'ALTA' || s === 'HIGH') return 'ALTA'
  if (s === 'MEDIA' || s === 'MEDIUM') return 'MEDIA'
  if (s === 'BAJA' || s === 'LOW') return 'BAJA'
  return null
}

// Deriva el estado de alerta del monitor para el sistema visual de markers.
// Retorna:
//   alertLevel: 'none' | 'base' | 'high' | 'low'
//   haloColor:  hex resuelto del token del tema activo (vacío si no aplica)
//   pulseActive: boolean (regla de 90s para sensor/reportant; WAITING para SOS;
//                prioridad ALTA para incident; false para bus/incident MEDIA)
// Ver MARKERS-DESIGN-SPEC.md §2.1 (tabla activación) y §3.1 (tabla pulse).
export function deriveAlertProps(monitor) {
  var entityType = monitor.entityType
  var data = monitor.data || {}
  var alertLevel = 'none'
  var haloColor = ''

  if (entityType === 'sensor' && monitor.status === 'alert') {
    alertLevel = 'base'
    haloColor = readCssVar('alert-danger')
  } else if (entityType === 'sensor' && monitor.status === 'reported') {
    // Sensor reportado: alerta atendida pendiente de cierre. Halo ámbar
    // sin pulse — se diferencia visualmente del `alert` (que es rojo +
    // pulse si es joven) y del `up` armado normal (sin halo). El operador
    // ve "algo pasó aquí, no cerrado" sin que reclame atención urgente.
    // AJUSTE-PALETA-MARKERS §4.3.
    alertLevel = 'resolved'
    haloColor = readCssVar('alert-warning')
  } else if (entityType === 'reportant') {
    alertLevel = 'base'
    haloColor = readCssVar('alert-sos')
  } else if (entityType === 'sos_call') {
    var st = data.status
    var finished = (st === 'CLIENT_END_CALL' || st === 'OPERATOR_END_CALL' || st === 'ENDED')
    if (!finished) {
      alertLevel = 'base'
      haloColor = readCssVar('alert-sos')
    }
  } else if (entityType === 'incident' && data.status === 'ACTIVO') {
    var pri = normalizePriority(data.attributes && data.attributes.prioridad)
    if (pri === 'ALTA') alertLevel = 'high'
    else if (pri === 'BAJA') alertLevel = 'low'
    else alertLevel = 'medium'  // MEDIA o ausente — solo halo interno (spec §2.2)
    // Halo tinted por categoría CNIE (paridad con el fondo del icono).
    // Si la categoría no resuelve a un token conocido, fallback a
    // alert-danger (rojo) para no romper incidents legacy.
    var slug = resolveIncidentGroup(data)
    var haloToken = INCIDENT_HALO_TOKEN[slug] || 'alert-danger'
    haloColor = readCssVar(haloToken) || readCssVar('alert-danger')
  } else if (entityType === 'bus' && monitor.status === 'alert') {
    alertLevel = 'base'
    haloColor = readCssVar('alert-warning')
  }

  // device: el overlay de eventos se maneja aparte (área 7 del PR),
  // NO por alertLevel del feature. Queda 'none' aquí siempre.

  // Pulse — regla de activación del spec §3.1
  var createdAtMs = parseTimestamp(data.createdAt || data.serverTime || data.startDate)
  var isYoung = createdAtMs > 0 && (Date.now() - createdAtMs) < 90000
  var pulseActive = false
  if (entityType === 'sos_call' && data.status === 'WAITING_OPERATOR') pulseActive = true
  else if (entityType === 'sensor' && alertLevel === 'base' && isYoung) pulseActive = true
  else if (entityType === 'reportant' && isYoung) pulseActive = true
  else if (entityType === 'incident' && alertLevel === 'high') pulseActive = true
  // incident MEDIA (alertLevel='medium') y BAJA ('low') NO pulsan (spec §3.1).
  // bus alert NO pulsa (halo estático). incident MEDIA/BAJA tampoco.

  return { alertLevel: alertLevel, haloColor: haloColor, pulseActive: pulseActive }
}

export function monitorToFeature(monitor, mapMode) {
  if (mapMode === undefined) mapMode = 'day'
  var hash = hashId(monitor.id)
  // labelPriority — clase declarativa 1..4 que las layers de texto usan en
  // filters para decidir si un feature recibe label "garantizado" (>=3,
  // sin colision detection) o entra al pool de collision detection normal.
  // Tabla:
  //   4 = incident, sos_call, reportant; sensor con status='alert'
  //   3 = cualquier otra entity con status='alert' (bus, device)
  //   2 = entity dinamica con status='online' (bus, device, etc.)
  //   1 = otros (estaticos, offline, down, idle)
  var et = monitor.entityType
  var labelPriority = 1
  if (et === 'incident' || et === 'sos_call' || et === 'reportant') {
    labelPriority = 4
  } else if (et === 'sensor' && monitor.status === 'alert') {
    labelPriority = 4
  } else if (monitor.status === 'alert') {
    labelPriority = 3
  } else if (!monitor.isStatic && monitor.status === 'online') {
    labelPriority = 2
  }

  // iconSortKey — POSITIVO. Lo leen las layers de iconos (icon-allow-overlap:
  // true) donde MapLibre dibuja MAYOR sort-key encima. labelPriority * 1M
  // garantiza que incidentes/SOS/sensor:alert queden visualmente arriba de
  // los markers vecinos. El hash rompe empates de forma estable dentro del
  // tier para evitar re-ranking entre frames.
  var iconSortKey = labelPriority * 1000000 + (hash % 1000000)

  // labelSortKey — NEGATIVO. Lo leen las layers de texto con
  // text-allow-overlap:false (UNIFIED_LABELS_COLLIDE) donde MapLibre
  // prioriza placement de MENOR sort-key. Negativo invierte la semantica
  // para que la priority alta GANE las colisiones, opuesto a iconSortKey.
  // La layer UNIFIED_LABELS_PRIORITY usa text-allow-overlap:true y no
  // depende de este valor.
  var labelSortKey = -labelPriority * 1000000 - (hash % 1000000)
  var alertProps = deriveAlertProps(monitor)
  return {
    // feature.id se omite intencionalmente: el source usa
    // promoteId:'monitorId' en su config, asi MapLibre toma la string
    // monitor.id (que es unique por construccion: entityType-rawId)
    // como identificador. Antes setabamos id:hashId(monitor.id) y con
    // ~19K monitors la birthday-paradox daba ~8% de probabilidad de
    // colision int32; updateData rechazaba toda la FeatureCollection
    // con "GeoJSON data is not compatible with updateData" en cada
    // diff posterior al primer setData.
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: monitor.coordinates,
    },
    properties: {
      monitorId: monitor.id,
      entityType: monitor.entityType,
      title: monitor.title,
      subtitle: monitor.subtitle,
      status: monitor.status,
      // iconStatus mapea el status semántico al sufijo del SVG. Para
      // entity-types con variantes distintas de up/down (incident por
      // prioridad, sos_call por estado de llamada), priorizamos el
      // estado granular antes del fallback genérico.
      iconStatus: deriveIconStatus(monitor),
      icon: monitor.icon,
      mapMode: mapMode,
      color: monitor.color,
      isStatic: monitor.isStatic,
      speed: monitor.data.position ? (monitor.data.position.speed || 0) : 0,
      course: monitor.data.position ? (monitor.data.position.course || 0) : 0,
      groupId: monitor.data.groupId != null ? Number(monitor.data.groupId) || monitor.data.groupId : '',
      // Timestamp como ms para filtros temporales del heatmap ("últimas 24 h").
      // Sólo tiene sentido en incidentes pero se expone siempre (0 si no hay).
      createdAtMs: parseTimestamp(monitor.data.createdAt || monitor.data.serverTime || monitor.data.startDate),
      labelPriority: labelPriority,
      iconSortKey: iconSortKey,
      labelSortKey: labelSortKey,
      // Defaults para el spread radial (overlap-spreader.js los sobreescribe
      // sólo en features que estén en un grupo de coords duplicadas). Se
      // setean aquí desde el inicio para que MapLibre tenga el tipo array
      // garantizado al evaluar las expresiones data-driven de icon-offset
      // y text-offset (sin esto, MapLibre puede caer al fallback del
      // coalesce silenciosamente cuando el feature no se ha pasado por
      // applySpreads todavía).
      iconOffsetPx: [0, 0],
      textOffsetEm: [0, 1.8],
      spreadActive: false,
      spreadGroupSize: 1,
      // Sistema visual de markers (MARKERS-DESIGN-SPEC.md):
      //   selected: único marker con drawer abierto (se actualiza desde MapV2
      //             con updateFeatureProperties al cambiar selectedItem).
      //   alertLevel: 'none' | 'base' | 'high' | 'low' — derivado del monitor.
      //   haloColor: hex del halo (resuelto del tema activo).
      //   pulseActive: si el halo externo debe pulsar ahora mismo.
      selected: false,
      alertLevel: alertProps.alertLevel,
      haloColor: alertProps.haloColor,
      pulseActive: alertProps.pulseActive,
      // Sistema de interconexión externa (carbyne/didi/uber/rapidsos)
      // o '' si la feature no aplica. El layer `unified-partner-badge`
      // filtra por `['has', 'partner']` y mapea el id al sprite del
      // logo precargado en icon-registry. La cadena vacía (no null)
      // evita que MapLibre la trate como `has`. Cuando el monitor no
      // tiene partner, omitimos la propiedad por completo:
      ...(monitor.partner ? { partner: monitor.partner } : {}),
    },
  }
}

function parseTimestamp(v) {
  if (!v) return 0
  if (typeof v === 'number') return v
  var t = Date.parse(v)
  return isNaN(t) ? 0 : t
}

// Flag del sistema declarativo de temas. Cuando ON, los SVGs vienen
// pre-pintados con nombres basados en el estado semántico (online,
// offline, alert, reported, etc.). Cuando OFF, los SVGs legacy solo
// tienen variantes up/down → deriveIconStatus colapsa al mapping viejo.
var USE_GENERATED_ICONS = String(
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_USE_GENERATED_ICONS) || ''
).toLowerCase() === 'true'

/**
 * Selecciona el sufijo del SVG que corresponde al estado granular del
 * monitor. Con flag ON devuelve el estado semántico (online/offline/
 * alert/reported/unknown/down/active/partial/inactive + prioridades de
 * incidente + estados de SOS). Con flag OFF mapea a up/down legacy.
 */
function deriveIconStatus(monitor) {
  var entityType = monitor.entityType
  var data = monitor.data || {}

  if (entityType === 'incident') {
    if (data.status !== 'ACTIVO') return USE_GENERATED_ICONS ? 'baja' : 'down'
    var pri = (data.attributes && data.attributes.prioridad)
    if (pri) {
      var p = String(pri).toUpperCase()
      if (p === 'ALTA' || p === 'HIGH') return 'alta'
      if (p === 'MEDIA' || p === 'MEDIUM') return USE_GENERATED_ICONS ? 'media' : 'up'
      if (p === 'BAJA' || p === 'LOW') return USE_GENERATED_ICONS ? 'baja' : 'up'
    }
    return USE_GENERATED_ICONS ? 'media' : 'up'
  }

  if (entityType === 'sos_call') {
    if (!USE_GENERATED_ICONS) {
      // Legacy: icon up para cualquier SOS; ENDED queda con iconStatus='up'
      // pero bajo una opacidad aparte.
      return 'up'
    }
    var s = data.status
    if (s === 'WAITING_OPERATOR') return 'WAITING_OPERATOR'
    if (s === 'IN_PROGRESS') return 'IN_PROGRESS'
    return 'ENDED'
  }

  if (entityType === 'reportant') {
    // Los reportants comparten los mismos sufijos que sos_call en el set
    // generado (WAITING_OPERATOR/IN_PROGRESS/ENDED). Sin esta rama
    // dedicada, caía al branch genérico que devuelve 'alert' (su status
    // semántico) y MapLibre no encontraba `reportant_*_alert.svg` →
    // se renderizaba solo el círculo de fondo sin icono interno.
    if (!USE_GENERATED_ICONS) return 'up'
    var rs = data.status
    if (rs === 'WAITING_OPERATOR') return 'WAITING_OPERATOR'
    if (rs === 'IN_PROGRESS') return 'IN_PROGRESS'
    if (rs === 'ENDED') return 'ENDED'
    return 'IN_PROGRESS'
  }

  // Estados normalizados del resto de entity types
  if (USE_GENERATED_ICONS) {
    // Devuelve el status tal cual — el generator creó variantes
    // `<icon>_day_<status>.svg` para cada combinación.
    return monitor.status || 'up'
  }
  return statusToIconSuffix(monitor.status)
}

/**
 * Map semantic status to SVG icon suffix.
 * El sistema declarativo de temas (AJUSTE-PALETA-MARKERS) amplía los
 * sufijos disponibles: `up`/`down` siguen para iconos legacy, y se
 * agregan `alert`/`reported`/`unknown`/`partial`/`inactive`/`active` y
 * los estados de prioridad del incidente (`alta`/`media`/`baja`) y SOS
 * (`WAITING_OPERATOR`/`IN_PROGRESS`/`ENDED`) para que el naming del SVG
 * pre-generado coincida con el estado del feature.
 */
function statusToIconSuffix(status) {
  switch (status) {
    case 'online': return 'up'
    case 'offline': return 'down'
    case 'alert': return 'alert'
    case 'reported': return 'reported'
    case 'unknown': return 'unknown'
    case 'down': return 'down'
    case 'active': return 'active'
    case 'partial': return 'partial'
    case 'inactive': return 'inactive'
    case 'idle': return 'down'
    default: return 'up'
  }
}

/**
 * Convert monitor ID string to stable numeric hash for MapLibre feature IDs.
 * @param {string} str
 * @returns {number}
 */
export function hashId(str) {
  var hash = 0
  for (var i = 0; i < str.length; i++) {
    var char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return Math.abs(hash)
}

/**
 * Create a partial GeoJSON feature for position updates.
 * @param {string} monitorId
 * @param {number[]} newCoords - [lng, lat]
 * @param {Object} extraProps
 * @returns {Object} GeoJSON Feature
 */
export function createPositionUpdate(monitorId, newCoords, extraProps) {
  if (!extraProps) extraProps = {}
  return {
    // feature.id se omite — el source usa promoteId:'monitorId'.
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: newCoords,
    },
    properties: Object.assign({ monitorId: monitorId }, extraProps),
  }
}
